// ── Shared sub-types ──────────────────────────────────────────────────────────

export interface ILeaveType {
  id: number;
  name: string;
}

export interface IWorkflowTemplate {
  id: number;
  name: string;
}

export interface IRequester {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string;
  isActive?: boolean;
}

export interface ICurrentApprover {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  isActive?: boolean;
}

export interface ICurrentNode {
  id: number;
  name: string;
}

// ── Asset entity sub-types ────────────────────────────────────────────────────

export interface IAssetTypeRef {
  id: number;
  name: string;
}

export interface IAssetRef {
  id: number;
  assetTag?: string;
  modelSoftware?: string;
  osVersion?: string;
  serialNumber?: string;
  status?: string;
  [key: string]: unknown;
}

export interface IConsumableRef {
  id: number;
  name: string;
  consumableTag?: string;
  unit?: string;
  stockLeft?: number;
  [key: string]: unknown;
}

// ── Entity data ───────────────────────────────────────────────────────────────

/**
 * Unified entity data shape covering both leave and asset workflow requests.
 * Leave-specific and asset-specific fields are optional; only one set is
 * populated depending on the module.
 */
export interface IEntityData {
  id?: number;
  status: string;

  // Leave-specific
  startDate?: string;
  endDate?: string;
  reasonOfLeave?: string;
  reasonOfRejection?: string | null;
  leaveType?: ILeaveType;

  // Asset-specific
  requestId?: string;
  assetCategory?: "regular" | "consumable";
  requestType?: string;
  priority?: string;
  description?: string;
  notes?: string;
  rejectionReason?: string | null;
  assetType?: IAssetTypeRef | null;
  asset?: IAssetRef | null;
  consumable?: IConsumableRef | null;
}

/** Strongly-typed alias for leave workflow entity data. */
export interface ILeaveEntityData extends IEntityData {
  id: number;
  startDate: string;
  endDate: string;
  reasonOfLeave: string;
  reasonOfRejection: string | null;
  leaveType: ILeaveType;
}

/** Strongly-typed alias for asset workflow entity data. */
export interface IAssetEntityData extends IEntityData {
  requestId: string;
  assetCategory: "regular" | "consumable";
  requestType: string;
  priority: string;
  description: string;
  notes: string;
  rejectionReason: string | null;
  assetType: IAssetTypeRef | null;
  asset: IAssetRef | null;
  consumable: IConsumableRef | null;
}

// ── Workflow request ──────────────────────────────────────────────────────────

export interface IWorkflowRequest {
  id: number;
  entityType: string;
  entityId: number;
  status: string;
  startedAt: string;
  currentStepDeadline: string | null;
  workflowTemplate: IWorkflowTemplate;
  requester: IRequester;
  currentApprover: ICurrentApprover | null;
  currentNode: ICurrentNode | null;
  hasNotificationIssue?: boolean;
  entityData: IEntityData;
}

// ── Workflow instance (workflow builder / template views) ─────────────────────

export interface IWorkflowTemplateRef {
  id: number;
  name: string;
}

export interface IWorkflowUser {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  profileImage: string | null;
  isActive?: boolean;
}

export interface IWorkflowNode {
  id: number;
  name: string;
  // Graph node kind (approval / parallel_approval / condition / …). Null on
  // legacy linear instances.
  type?: string | null;
}

// One member of a parallel-approval gate, from the instance's active_approvers.
export interface IActiveApprover {
  userId: number;
  role?: string | null;
  status: string;
  actedAt?: string | null;
}

export interface IWorkflowEntityData {
  id: number;
  startDate: string;
  endDate: string;
  status: string;
  reasonOfLeave: string;
  reasonOfRejection: string | null;
  leaveType: {
    id: number;
    name: string;
  };
}

export interface IWorkflowInstance {
  id: number;
  entityType: string;
  entityId: number;
  status: string;
  startedAt: string;
  currentStepDeadline: string | null;
  workflowTemplate: IWorkflowTemplateRef;
  workflowTemplateVersion: {
    id: number;
    versionNumber: number;
    publishedAt: string | null;
  } | null;
  requester: IWorkflowUser;
  currentApprover: IWorkflowUser | null;
  currentNode: IWorkflowNode | null;
  // Populated while the instance sits on a parallel-approval gate; each entry is
  // one of the approvers who must (or may) act.
  activeApprovers?: IActiveApprover[] | null;
  hasNotificationIssue?: boolean;
  lastErrorMessage?: string | null;
  lastErrorAt?: string | null;
  errorRetryCount?: number;
  lastAttemptedAction?: string | null;
  entityData: IWorkflowEntityData;
}

export interface IWorkflowVersionPublisher {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
}

export interface IWorkflowVersion {
  id: number;
  versionNumber: number;
  name: string;
  description: string | null;
  isLatest: boolean;
  publishedAt: string;
  publishedBy: IWorkflowVersionPublisher | null;
  instanceCount: number;
}

export interface IWorkflowVersionListMeta {
  template: {
    id: number;
    name: string;
    module: string;
    status: string;
  };
}

export interface IWorkflowInstanceStats {
  totalInstances: number;
  activeInstances: number;
  overdueInstances: number;
  avgResolutionTimeHours: number;
  byStatus: Record<string, number>;
  byModule: Record<string, number>;
}

// ── Issue filter ─────────────────────────────────────────────────────────────

export type WorkflowIssueFilter = "hide" | "show" | "only";

// ── Audit trail ──────────────────────────────────────────────────────────────

export type WorkflowAuditEventType =
  | "created"
  | "step_advanced"
  | "approved"
  | "rejected"
  | "cancelled"
  | "changes_requested"
  | "resubmitted"
  | "delegated"
  | "overdue_marked"
  | "escalated"
  | "orphan_reassigned"
  | "error_stamped"
  | "error_cleared"
  | "admin_override";

export type WorkflowAuditActorType = "user" | "cron" | "engine" | "system";

export interface IWorkflowAuditActor {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
}

export interface IWorkflowAuditNodeRef {
  id: number;
  name: string;
}

export interface IWorkflowAuditEntry {
  id: number;
  eventType: WorkflowAuditEventType;
  fromStatus: string | null;
  toStatus: string | null;
  fromNode: IWorkflowAuditNodeRef | null;
  toNode: IWorkflowAuditNodeRef | null;
  fromApprover: IWorkflowAuditActor | null;
  toApprover: IWorkflowAuditActor | null;
  actorType: WorkflowAuditActorType;
  actor: IWorkflowAuditActor | null;
  reason: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

// ── Approval-steps sidebar payload ───────────────────────────────────────────

export interface IApprovalStepUser {
  id: number;
  firstName: string;
  lastName: string;
  profileImage: string | null;
}

// A resolved member of a parallel-approval step (user details + per-person
// decision state).
export interface IApprovalStepApprover extends IApprovalStepUser {
  role?: string | null;
  status: string;
  actedAt?: string | null;
}

export interface IApprovalStep {
  stepOrder: number;
  stepName: string;
  // Graph node kind. "parallel_approval" → render the `approvers` set; anything
  // else (or null on legacy instances) → a single `approver`.
  type?: string | null;
  positionName: string | null;
  approver: IApprovalStepUser | null;
  // Present for parallel-approval steps: everyone on the gate + their decisions.
  approvers?: IApprovalStepApprover[] | null;
  branch: string | null;
  status: string;
  isCurrent: boolean;
  actionAt: string | null;
  comments: string | null;
}

export interface IApprovalSteps {
  instanceId: number;
  startingPoint: {
    requester: IApprovalStepUser | null;
    condition: Record<string, unknown>;
  };
  totalSteps: number;
  currentStep: number | null;
  instanceStatus: string;
  lastChangesRequestedComment: string | null;
  lastChangesRequestedBy: IApprovalStepUser | null;
  lastChangesRequestedDates: {
    startDate: string | null;
    endDate: string | null;
  } | null;
  steps: IApprovalStep[];
}

/** Response shape for GET /company/workflows/instances/entity/:entityType/:entityId */
export interface IWorkflowInstanceByEntityResponse {
  instance: IWorkflowInstance | null;
  approvalSteps: IApprovalSteps | null;
}

export enum WorkflowInstanceStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  APPROVED = "approved",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
  CHANGES_REQUESTED = "changes_requested",
  OVERDUE = "overdue",
  ERROR = "error",
}
